/**
 * One state, said three ways at once.
 *
 * Colour, shape and word. Never colour alone: a reader who cannot separate the
 * two colours this product uses still has to tell a coin that is ready from one
 * that is quarantined, and a solid chip differs from a tinted one and from a
 * dashed one whatever colour they are. The `outlined` weight is drawn tinted —
 * the terminal mocks' chip — since 2026-10-03; the name stays for its callers.
 *
 * The tones are roles, not colours. `active` is the accent, because a slot
 * holding a symbol is not a result; `ready` is the positive scale, because it
 * is the state the strategy is looking for. Nothing here reaches for a hex.
 */

export type BadgeTone = "ready" | "active" | "warning" | "danger" | "muted";
export type BadgeShape = "filled" | "outlined" | "dashed";

const TONE: Record<BadgeTone, Record<BadgeShape, string>> = {
  ready: {
    filled: "bg-status-positive text-background-base",
    outlined: "bg-status-positive/15 text-status-positive",
    dashed: "border border-dashed border-status-positive text-status-positive",
  },
  active: {
    filled: "bg-action-primary text-text-on-action",
    outlined: "bg-action-primary/15 text-action-primary",
    dashed: "border border-dashed border-action-primary text-action-primary",
  },
  warning: {
    filled: "bg-status-warning text-background-base",
    outlined: "bg-status-warning/15 text-status-warning",
    dashed: "border border-dashed border-status-warning text-status-warning",
  },
  danger: {
    filled: "bg-status-negative text-background-base",
    outlined: "bg-status-negative/15 text-status-negative",
    dashed: "border border-dashed border-status-negative text-status-negative",
  },
  muted: {
    filled: "bg-surface-selected text-text-secondary",
    outlined: "border border-border-strong text-text-muted",
    dashed: "border border-dashed border-border-strong text-text-muted",
  },
};

type Props = {
  readonly children: React.ReactNode;
  readonly tone: BadgeTone;
  readonly shape?: BadgeShape | undefined;
  readonly testId?: string;
  /**
   * Extra `data-*` attributes, for the one case where a screen needs to assert
   * a fact about the badge that its text does not carry — which generation a
   * pool name belongs to, for instance. Written as data attributes rather than
   * as a class so a test reads the claim rather than the styling.
   */
  readonly dataset?: Readonly<Record<string, string>> | undefined;
};

/**
 * Which weight a tone is drawn at when the caller does not say.
 *
 * Fill is loud, so it is spent on the states that are asking for something.
 * A table where nineteen slots read "работает" in filled accent and the one
 * paused slot is a thin outline has the normal case shouting and the exception
 * whispering — which is the opposite of what a status column is for.
 */
function weight(tone: BadgeTone): BadgeShape {
  return tone === "warning" || tone === "danger" ? "filled" : "outlined";
}

export function StatusBadge({
  children,
  tone,
  shape,
  testId,
  dataset,
}: Props): React.JSX.Element {
  const drawn = shape ?? weight(tone);
  const attributes = Object.fromEntries(
    Object.entries(dataset ?? {}).map(([name, value]) => [`data-${name}`, value]),
  );
  return (
    <span
      data-testid={testId}
      {...attributes}
      className={`inline-flex shrink-0 items-center rounded-row px-1.5 py-0.5 text-helper-sm font-medium whitespace-nowrap ${TONE[tone][drawn]}`}
    >
      {children}
    </span>
  );
}
